import { createAsyncThunk } from '@reduxjs/toolkit';
import apiCall from 'utils/api';
import { InvoiceList, Invoice } from './types';

export const fetchInvoices = createAsyncThunk(
  'invoice/fetchInvoices',
  async (
    {
      page,
      pageSize,
      search,
      startDate,
      endDate,
    }: {
      page: number;
      pageSize: number;
      search?: string;
      startDate?: string;
      endDate?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      let url = `/api/invoice/list/web/?page=${page}&page_size=${pageSize}`;
      if (search) {
        url += `&search=${search}`;
      }
      if (startDate) {
        url += `&start_date=${startDate}`;
      }
      if (endDate) {
        url += `&end_date=${endDate}`;
      }
      const response = await apiCall<{ data: InvoiceList }>('get', url);
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch invoices');
    }
  }
);

export const fetchOrgInvoices = createAsyncThunk(
  'invoice/fetchOrgInvoices',
  async (
    {
      page,
      pageSize,
      facilityId,
      startDate,
      endDate,
    }: {
      page: number;
      pageSize: number;
      facilityId?: number;
      startDate?: string;
      endDate?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      let url = `/api/invoice/list/organization/?page=${page}&page_size=${pageSize}`;
      if (facilityId) {
        url += `&facility_id=${facilityId}`;
      }
      if (startDate) {
        url += `&start_date=${startDate}`;
      }
      if (endDate) {
        url += `&end_date=${endDate}`;
      }
      const response = await apiCall<{ data: InvoiceList }>('get', url);
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch organization invoices');
    }
  }
);

export const generateOrgInvoice = createAsyncThunk(
  'invoice/generateOrgInvoice',
  async (
    {
      facilityId,
    }: {
      facilityId: number;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await apiCall<any>('post', '/api/invoice/generate/organization/', {
        facility_id: facilityId,
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to generate invoice');
    }
  }
);

export const publishInvoice = createAsyncThunk(
  'invoice/publishInvoice',
  async (
    {
      invoiceId,
      facilityId,
    }: {
      invoiceId: number;
      facilityId: number;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await apiCall<any>('put', '/api/invoice/publish/', {
        invoice_id: invoiceId,
        facility_id: facilityId,
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to publish invoice');
    }
  }
);


export const markInvoicePaid = createAsyncThunk(
  'invoice/markInvoicePaid',
  async (
    {
      invoiceId,
      facilityId,
    }: {
      invoiceId: number;
      facilityId?: number;
    },
    { rejectWithValue },
  ) => {
    try {
      const payload: any = { invoice_id: invoiceId };
      if (facilityId) payload.facility_id = facilityId;

      const response = await apiCall<any>('put', '/api/invoice/mark-paid/', payload);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to mark invoice as paid');
    }
  }
);

export const fetchFacilityInvoices = createAsyncThunk(
  'invoice/fetchFacilityInvoices',
  async (
    {
      page,
      pageSize,
      facilityId,
      search,
      startDate,
      endDate,
    }: {
      page: number;
      pageSize: number;
      facilityId: number;
      search?: string;
      startDate?: string;
      endDate?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('page_size', pageSize.toString());

      if (search) params.append('search', search);
      // Only append start/end date if they are truthy strings
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);

      const url = `/api/invoice/list/${facilityId}/?${params.toString()}`;
      const response = await apiCall<{ data: InvoiceList }>('get', url);
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch facility invoices');
    }
  }
);

export const fetchInvoiceDetails = createAsyncThunk(
  'invoice/fetchInvoiceDetails',
  async (invoiceId: number, { rejectWithValue }) => {
    try {
      const response = await apiCall<{ data: Invoice }>('get', `/api/invoice/details/${invoiceId}/`);
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch invoice details');
    }
  }
);
